import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { CalendarClock, Plus, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const TIMEZONES = ['Africa/Addis_Ababa', 'UTC', 'Europe/London', 'America/New_York', 'Asia/Dubai'];
const PLACEMENTS = ['right_sidebar', 'left_sidebar', 'homepage_banner', 'agencies_inline_mobile'];

export function BannerScheduleManager({ campaignId }: { campaignId?: string }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [pickedCampaign, setPickedCampaign] = useState<string>(campaignId || '');
  const [placement, setPlacement] = useState('right_sidebar');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [tz, setTz] = useState('Africa/Addis_Ababa');
  const [submitting, setSubmitting] = useState(false);

  const { data: campaigns } = useQuery({
    queryKey: ['all-campaigns-for-schedule'],
    queryFn: async () => {
      const { data } = await supabase.from('ad_campaigns' as any).select('id, title, placement, created_by').order('created_at', { ascending: false }).limit(200);
      return (data as any[]) || [];
    },
  });

  const { data: schedules, isLoading } = useQuery({
    queryKey: ['banner-schedules', campaignId],
    queryFn: async () => {
      let q = supabase.from('banner_schedules' as any).select('*, ad_campaigns(title)').order('scheduled_start', { ascending: false });
      if (campaignId) q = q.eq('campaign_id', campaignId);
      const { data, error } = await q;
      if (error) throw error;
      return (data as any[]) || [];
    },
  });

  const submit = async () => {
    if (!pickedCampaign || !start || !end) { toast.error('Pick campaign and dates'); return; }
    setSubmitting(true);
    const { error } = await supabase.from('banner_schedules' as any).insert({
      campaign_id: pickedCampaign,
      placement,
      scheduled_start: new Date(start).toISOString(),
      scheduled_end: new Date(end).toISOString(),
      timezone: tz,
      created_by: user?.id,
    });
    setSubmitting(false);
    if (error) { toast.error(error.message); return; }
    toast.success('Schedule created — banner will activate automatically.');
    setOpen(false);
    qc.invalidateQueries({ queryKey: ['banner-schedules'] });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarClock className="h-5 w-5 text-primary" /> Banner Schedules
          <Button size="sm" className="ml-auto" onClick={() => setOpen(true)}>
            <Plus className="h-4 w-4 mr-1" /> New schedule
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? <Skeleton className="h-32" /> : !schedules?.length ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No schedules yet. Create one to auto-activate banners.</p>
        ) : (
          <div className="space-y-2">
            {schedules.map((s: any) => (
              <div key={s.id} className="border rounded-lg p-3 flex flex-wrap items-center gap-3 text-sm">
                <Badge variant={s.is_active ? 'default' : 'secondary'}>{s.is_active ? 'Live' : new Date(s.scheduled_start) > new Date() ? 'Scheduled' : 'Ended'}</Badge>
                <span className="font-medium">{s.ad_campaigns?.title || s.campaign_id?.slice(0, 8)}</span>
                <Badge variant="outline" className="text-[10px]">{s.placement.replace(/_/g, ' ')}</Badge>
                <span className="text-xs text-muted-foreground">
                  {format(new Date(s.scheduled_start), 'd MMM HH:mm')} → {format(new Date(s.scheduled_end), 'd MMM HH:mm')} ({s.timezone})
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Schedule banner activation</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {!campaignId && (
              <div>
                <Label>Banner</Label>
                <Select value={pickedCampaign} onValueChange={setPickedCampaign}>
                  <SelectTrigger><SelectValue placeholder="Pick a banner" /></SelectTrigger>
                  <SelectContent>
                    {(campaigns || []).map((c: any) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Placement</Label>
              <Select value={placement} onValueChange={setPlacement}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PLACEMENTS.map((p) => <SelectItem key={p} value={p}>{p.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start</Label>
                <Input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} />
              </div>
              <div>
                <Label>End</Label>
                <Input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} />
              </div>
            </div>
            <div>
              <Label>Timezone</Label>
              <Select value={tz} onValueChange={setTz}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TIMEZONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>Cancel</Button>
            <Button onClick={submit} disabled={submitting}>
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Schedule
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
